/**
 * Request Service
 * Handles CRUD operations for token requests using Supabase backend
 */
import supabaseService from './supabaseService';
import logger from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';
import emailService from './emailService';
import employeeService from './employeeService';

const REQUESTS_TABLE = 'requests';

class RequestService {
  async getAllRequests() {
    try {
      logger.info('Fetching all requests', { operation: 'getAllRequests' });

      const requests = await supabaseService.select(REQUESTS_TABLE, {
        order: { column: 'createdAt', ascending: true },
      });

      logger.info('Requests fetched successfully', {
        operation: 'getAllRequests',
        count: requests?.length || 0,
      });

      return requests || [];
    } catch (error) {
      logger.error('Error fetching requests', error, { operation: 'getAllRequests' });
      throw error;
    }
  }

  async getRequestById(requestId) {
    try {
      logger.info('Fetching request by ID', { requestId, operation: 'getRequestById' });

      const request = await supabaseService.select(REQUESTS_TABLE, {
        filters: [{ column: 'id', value: requestId }],
        single: true,
      });

      if (!request) {
        logger.warning('Request not found', { requestId, operation: 'getRequestById' });
        return null;
      }

      return request;
    } catch (error) {
      logger.error('Error fetching request', error, { requestId, operation: 'getRequestById' });
      throw error;
    }
  }

  async getRequestsByEmployee(employeeId) {
    try {
      logger.info('Fetching requests by employee', {
        employeeId,
        operation: 'getRequestsByEmployee',
      });

      const requests = await supabaseService.select(REQUESTS_TABLE, {
        filters: [{ column: 'employeeId', value: employeeId }],
        order: { column: 'createdAt', ascending: true },
      });

      logger.info('Employee requests fetched', {
        employeeId,
        count: requests?.length || 0,
        operation: 'getRequestsByEmployee',
      });

      return requests || [];
    } catch (error) {
      logger.error('Error fetching employee requests', error, {
        employeeId,
        operation: 'getRequestsByEmployee',
      });
      throw error;
    }
  }

  async getRequestsByManager(managerId) {
    try {
      logger.info('Fetching requests by manager', {
        managerId,
        operation: 'getRequestsByManager',
      });

      const requests = await supabaseService.select(REQUESTS_TABLE, {
        filters: [
          { column: 'managerId', value: managerId },
          {
            column: 'status',
            operator: 'in',
            value: ['pending', 'cancellation_requested'],
          },
        ],
        order: { column: 'createdAt', ascending: true },
      });

      logger.info('Manager requests fetched', {
        managerId,
        count: requests?.length || 0,
        operation: 'getRequestsByManager',
      });

      return requests || [];
    } catch (error) {
      logger.error('Error fetching manager requests', error, {
        managerId,
        operation: 'getRequestsByManager',
      });
      throw error;
    }
  }

  async createRequest(requestData) {
    try {
      logger.info('Creating new request', { operation: 'createRequest', data: requestData });

      if (!requestData.employeeId || !requestData.startDate || !requestData.endDate) {
        throw new Error('Missing required fields: employeeId, startDate, endDate');
      }

      const timestamp = new Date().toISOString();
      const newRequest = {
        id: uuidv4(),
        employeeId: requestData.employeeId,
        managerId: requestData.managerId || null,
        startDate: requestData.startDate,
        endDate: requestData.endDate,
        days: requestData.days || 1,
        status: 'pending',
        reason: requestData.reason || '',
        createdAt: timestamp,
        updatedAt: timestamp,
        reviewedAt: null,
        reviewedBy: null,
        rejectionReason: null,
        cancellationReason: null,
        originalStatus: null,
        cancellationRejectionReason: null,
      };

      const inserted = await supabaseService.insert(REQUESTS_TABLE, newRequest);
      const createdRequest = Array.isArray(inserted) ? inserted[0] : inserted;

      logger.info('Request created successfully', {
        requestId: createdRequest?.id,
        operation: 'createRequest',
      });

      if (newRequest.managerId) {
        try {
          const [employee, manager] = await Promise.all([
            employeeService.getEmployeeById(newRequest.employeeId),
            employeeService.getEmployeeById(newRequest.managerId),
          ]);

          if (employee && manager) {
            await emailService.sendNewRequestNotificationEmail(manager, employee, createdRequest || newRequest);
          }
        } catch (emailError) {
          logger.warning('Failed to send new request notification email', {
            requestId: newRequest.id,
            error: emailError?.message,
          });
        }
      }

      return createdRequest || newRequest;
    } catch (error) {
      logger.error('Error creating request', error, { operation: 'createRequest' });
      throw error;
    }
  }

  async updateRequest(requestId, updates) {
    try {
      logger.info('Updating request', { requestId, updates, operation: 'updateRequest' });

      const updatePayload = { ...updates, updatedAt: new Date().toISOString() };

      if (updates.status && ['approved', 'rejected', 'cancelled'].includes(updates.status)) {
        updatePayload.reviewedAt = new Date().toISOString();
        updatePayload.reviewedBy = updates.reviewedBy || updatePayload.reviewedBy || null;
      }

      const updatedRequest = await supabaseService.update(REQUESTS_TABLE, updatePayload, {
        filters: [{ column: 'id', value: requestId }],
        single: true,
      });

      if (!updatedRequest) {
        throw new Error('Request not found');
      }

      logger.info('Request updated successfully', { requestId, operation: 'updateRequest' });
      return updatedRequest;
    } catch (error) {
      logger.error('Error updating request', error, { requestId, operation: 'updateRequest' });
      throw error;
    }
  }

  async approveRequest(requestId, reviewedBy) {
    try {
      logger.info('Approving request', { requestId, reviewedBy, operation: 'approveRequest' });

      const request = await this.updateRequest(requestId, {
        status: 'approved',
        reviewedBy,
      });

      try {
        const employee = await employeeService.getEmployeeById(request.employeeId);
        if (employee) {
          await emailService.sendRequestApprovalEmail(employee, request);
        }
      } catch (emailError) {
        logger.warning('Failed to send approval email', {
          requestId,
          error: emailError?.message,
        });
      }

      return request;
    } catch (error) {
      logger.error('Error approving request', error, {
        requestId,
        operation: 'approveRequest',
      });
      throw error;
    }
  }

  async rejectRequest(requestId, reviewedBy, reason = '') {
    try {
      logger.info('Rejecting request', {
        requestId,
        reviewedBy,
        reason,
        operation: 'rejectRequest',
      });

      const request = await this.updateRequest(requestId, {
        status: 'rejected',
        reviewedBy,
        rejectionReason: reason,
      });

      try {
        const employee = await employeeService.getEmployeeById(request.employeeId);
        if (employee) {
          await emailService.sendRequestRejectionEmail(employee, request, reason);
        }
      } catch (emailError) {
        logger.warning('Failed to send rejection email', {
          requestId,
          error: emailError?.message,
        });
      }

      return request;
    } catch (error) {
      logger.error('Error rejecting request', error, {
        requestId,
        operation: 'rejectRequest',
      });
      throw error;
    }
  }

  async requestCancellation(requestId, reason = '') {
    try {
      logger.info('Requesting cancellation', {
        requestId,
        reason,
        operation: 'requestCancellation',
      });

      const request = await this.getRequestById(requestId);
      if (!request) {
        throw new Error('Request not found');
      }

      if (!['pending', 'approved'].includes(request.status)) {
        throw new Error('Only pending or approved requests can be cancelled');
      }

      const updatedRequest = await this.updateRequest(requestId, {
        status: 'cancellation_requested',
        cancellationReason: reason,
        originalStatus: request.status,
      });

      // Send email notification to manager about cancellation request
      if (updatedRequest.managerId) {
        try {
          const [employee, manager] = await Promise.all([
            employeeService.getEmployeeById(updatedRequest.employeeId),
            employeeService.getEmployeeById(updatedRequest.managerId),
          ]);

          if (employee && manager) {
            await emailService.sendCancellationRequestNotificationEmail(
              manager,
              employee,
              updatedRequest,
              reason
            );
          }
        } catch (emailError) {
          logger.warning('Failed to send cancellation request notification email', {
            requestId: updatedRequest.id,
            error: emailError?.message,
          });
        }
      }

      return updatedRequest;
    } catch (error) {
      logger.error('Error requesting cancellation', error, {
        requestId,
        operation: 'requestCancellation',
      });
      throw error;
    }
  }

  async approveCancellation(requestId, reviewedBy) {
    try {
      logger.info('Approving cancellation', {
        requestId,
        reviewedBy,
        operation: 'approveCancellation',
      });

      const request = await this.updateRequest(requestId, {
        status: 'cancelled',
        reviewedBy,
        reviewedAt: new Date().toISOString(),
      });

      try {
        const employee = await employeeService.getEmployeeById(request.employeeId);
        if (employee) {
          await emailService.sendCancellationApprovalEmail(employee, request);
        }
      } catch (emailError) {
        logger.warning('Failed to send cancellation approval email', {
          requestId,
          error: emailError?.message,
        });
      }

      return request;
    } catch (error) {
      logger.error('Error approving cancellation', error, {
        requestId,
        operation: 'approveCancellation',
      });
      throw error;
    }
  }

  async rejectCancellation(requestId, reviewedBy, reason = '') {
    try {
      logger.info('Rejecting cancellation', {
        requestId,
        reviewedBy,
        reason,
        operation: 'rejectCancellation',
      });

      const request = await this.getRequestById(requestId);
      if (!request) {
        throw new Error('Request not found');
      }

      const originalStatus = request.originalStatus || 'pending';

      const updatedRequest = await this.updateRequest(requestId, {
        status: originalStatus,
        reviewedBy,
        reviewedAt: new Date().toISOString(),
        cancellationRejectionReason: reason,
        originalStatus: null,
      });

      try {
        const employee = await employeeService.getEmployeeById(updatedRequest.employeeId);
        if (employee) {
          await emailService.sendCancellationRejectionEmail(employee, updatedRequest, reason);
        }
      } catch (emailError) {
        logger.warning('Failed to send cancellation rejection email', {
          requestId,
          error: emailError?.message,
        });
      }

      return updatedRequest;
    } catch (error) {
      logger.error('Error rejecting cancellation', error, {
        requestId,
        operation: 'rejectCancellation',
      });
      throw error;
    }
  }

  async deleteRequest(requestId) {
    try {
      logger.info('Deleting request', { requestId, operation: 'deleteRequest' });

      const deleted = await supabaseService.remove(REQUESTS_TABLE, {
        filters: [{ column: 'id', value: requestId }],
        single: true,
      });

      if (!deleted) {
        throw new Error('Request not found');
      }

      logger.info('Request deleted successfully', { requestId, operation: 'deleteRequest' });
      return true;
    } catch (error) {
      logger.error('Error deleting request', error, { requestId, operation: 'deleteRequest' });
      throw error;
    }
  }

  async getRequestsByDateRange(startDate, endDate) {
    try {
      logger.info('Fetching requests by date range', {
        startDate,
        endDate,
        operation: 'getRequestsByDateRange',
      });

      const requests = await this.getAllRequests();
      const filtered = requests.filter((req) => {
        const reqStart = new Date(req.startDate);
        const reqEnd = new Date(req.endDate);
        const rangeStart = new Date(startDate);
        const rangeEnd = new Date(endDate);

        return (
          (reqStart >= rangeStart && reqStart <= rangeEnd) ||
          (reqEnd >= rangeStart && reqEnd <= rangeEnd) ||
          (reqStart <= rangeStart && reqEnd >= rangeEnd)
        );
      });

      logger.info('Requests by date range fetched', {
        startDate,
        endDate,
        count: filtered.length,
        operation: 'getRequestsByDateRange',
      });

      return filtered;
    } catch (error) {
      logger.error('Error fetching requests by date range', error, {
        operation: 'getRequestsByDateRange',
      });
      throw error;
    }
  }
}

const requestService = new RequestService();
export default requestService;

